import React, { useMemo } from 'react'
import { KodiakFarm } from 'types/farms'

import { StakingData } from '../config'
import { RewardsProvider, useRewards } from '../context/RewardContext'
import { useIslandContext } from '../hooks/useIslandContext'
import EmptyStateCard from './cards/EmptyStateCard'
import MigrationCard from './cards/MigrationCard'
import RewardsCard from './cards/RewardsCard'
import FarmClaimModal from './modals/FarmClaimModal'
import MigrationModal from './modals/MigrationModal'
import VaultClaimModal from './modals/VaultClaimModal'

const RewardsCardContent: React.FC = () => {
  const {
    viewType,
    activeModal,
    kodiakFarmEarned,
    rewardVaultEarned,
    rewardVaultRewardToken,
    hasKodiakFarmEarnings,
    hasRewardVaultEarnings,
    anyTransactionInProgress,
    claimTxState,
    openModal,
  } = useRewards()
  const { isRewardVaultStakingDisabled } = useIslandContext()

  const handleKodiakFarmClaimClick = () => openModal('farmClaim')
  const handleRewardVaultClaimClick = () => openModal('vaultClaim')
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol || rewardVaultEarned[0]?.amount?.currency.symbol
  const buttonText = rewardVaultRewardSymbol ? `Claim ${rewardVaultRewardSymbol} Rewards` : 'Claim Rewards'

  return (
    <>
      {activeModal === 'migration' && <MigrationModal />}
      {activeModal === 'farmClaim' && <FarmClaimModal />}
      {activeModal === 'vaultClaim' && <VaultClaimModal />}

      {(viewType === 'migration' || (viewType === 'migration-prompt' && !isRewardVaultStakingDisabled)) && (
        <MigrationCard />
      )}

      {viewType === 'kodiakFarm' && (
        <RewardsCard
          earnedItems={kodiakFarmEarned}
          hasEarnings={hasKodiakFarmEarnings}
          buttonText="Claim"
          anyTransactionInProgress={anyTransactionInProgress}
          txError={claimTxState.error}
          onClaimClick={handleKodiakFarmClaimClick}
        />
      )}

      {viewType === 'rewardVault' && (
        <>
          <RewardsCard
            earnedItems={rewardVaultEarned}
            hasEarnings={hasRewardVaultEarnings}
            buttonText={buttonText}
            anyTransactionInProgress={anyTransactionInProgress}
            txError={claimTxState.error}
            onClaimClick={handleRewardVaultClaimClick}
          />
        </>
      )}

      {viewType === 'empty' && <EmptyStateCard />}
    </>
  )
}

interface IslandFarmEarnedCardProps {
  kodiakFarm?: KodiakFarm | null
  hasKodiakFarm?: boolean
  rewardVault?: any | null
  stakingType?: StakingData['stakingType']
  shouldShowMigration?: boolean
}

const IslandFarmEarnedCard: React.FC<IslandFarmEarnedCardProps> = ({
  kodiakFarm,
  hasKodiakFarm,
  rewardVault,
  stakingType = 'none',
  shouldShowMigration = false,
}) => {
  const actualStakingType = useMemo(() => {
    if (rewardVault && stakingType === 'rewardVault') return 'rewardVault'
    if (kodiakFarm && stakingType === 'kodiakFarm') return 'kodiakFarm'
    return 'none'
  }, [rewardVault, kodiakFarm, stakingType])

  return (
    <RewardsProvider
      kodiakFarm={kodiakFarm}
      hasKodiakFarm={hasKodiakFarm}
      rewardVault={rewardVault}
      stakingType={actualStakingType}
      shouldShowMigration={shouldShowMigration}
    >
      <RewardsCardContent />
    </RewardsProvider>
  )
}

export default IslandFarmEarnedCard
